import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  GitBranch,
  Eye,
  Download,
  RotateCcw,
  Plus,
  User,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  RefreshCw,
  X,
  AlertCircle,
  Edit
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  handleTextInputChange,
  handleNumberInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import { getQuotationStatusClasses } from '../../../utils/quotationStatusUtils';

const VersionHistory = () => {
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showCreateVersionModal, setShowCreateVersionModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [editingVersion, setEditingVersion] = useState(null);
  const [downloadingVersion, setDownloadingVersion] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [errors, setErrors] = useState({});
  const [newVersionNotes, setNewVersionNotes] = useState('');

  // Mock quote data
  const quote = {
    id: quoteId || 'Q-2024-001',
    customer: 'ABC Pharmaceuticals',
    title: 'Medical Equipment Supply Quotation',
    currentVersion: 3,
    totalVersions: 3
  };

  // Changed from const to useState to allow updates
  const [versions, setVersions] = useState([
    {
      id: 1,
      version: 1,
      status: 'Rejected',
      createdBy: 'John Smith',
      createdAt: '2024-01-10 10:30 AM',
      changes: 'Initial quote creation',
      totalValue: 145000,
      itemsCount: 3,
      items: [
        { name: 'Medical Scanner X1', quantity: 1, unitPrice: 75000, totalPrice: 75000 },
        { name: 'Diagnostic Kit Pro', quantity: 2, unitPrice: 25000, totalPrice: 50000 },
        { name: 'Safety Equipment Set', quantity: 1, unitPrice: 20000, totalPrice: 20000 }
      ],
      terms: 'Payment: 30 days net. Delivery: 2-3 weeks. Warranty: 1 year.',
      communications: [
        { channel: 'Email', sentAt: '2024-01-10 11:00 AM', status: 'Delivered' },
        { channel: 'WhatsApp', sentAt: '2024-01-10 11:05 AM', status: 'Delivered' }
      ]
    },
    {
      id: 2,
      version: 2,
      status: 'Seen',
      createdBy: 'John Smith',
      createdAt: '2024-01-12 2:15 PM',
      changes: 'Updated pricing and added new products',
      totalValue: 150000,
      itemsCount: 4,
      items: [
        { name: 'Medical Scanner X1', quantity: 1, unitPrice: 75000, totalPrice: 75000 },
        { name: 'Diagnostic Kit Pro', quantity: 2, unitPrice: 25000, totalPrice: 50000 },
        { name: 'Safety Equipment Set', quantity: 1, unitPrice: 15000, totalPrice: 15000 },
        { name: 'Maintenance Kit', quantity: 1, unitPrice: 10000, totalPrice: 10000 }
      ],
      terms: 'Payment: 30 days net. Delivery: 2-3 weeks. Warranty: 1 year. Installation included.',
      communications: [
        { channel: 'Email', sentAt: '2024-01-12 2:30 PM', status: 'Delivered' },
        { channel: 'WhatsApp', sentAt: '2024-01-12 2:35 PM', status: 'Delivered' }
      ]
    },
    {
      id: 3,
      version: 3,
      status: 'Sent',
      createdBy: 'John Smith',
      createdAt: '2024-01-15 9:45 AM',
      changes: 'Final pricing adjustments and terms update',
      totalValue: 150000,
      itemsCount: 4,
      items: [
        { name: 'Medical Scanner X1', quantity: 1, unitPrice: 75000, totalPrice: 75000 },
        { name: 'Diagnostic Kit Pro', quantity: 2, unitPrice: 25000, totalPrice: 50000 },
        { name: 'Safety Equipment Set', quantity: 1, unitPrice: 15000, totalPrice: 15000 },
        { name: 'Maintenance Kit', quantity: 1, unitPrice: 10000, totalPrice: 10000 }
      ],
      terms: 'Payment: 45 days net. Delivery: 2-3 weeks. Warranty: 2 years. Installation and training included.',
      communications: [
        { channel: 'Email', sentAt: '2024-01-15 10:30 AM', status: 'Delivered' },
        { channel: 'WhatsApp', sentAt: '2024-01-15 10:35 AM', status: 'Delivered' }
      ]
    }
  ]);

  // Use shared quotation status colors (supports all backend enum values)

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleVersionSelect = (versionId) => {
    if (selectedVersions.includes(versionId)) {
      setSelectedVersions(selectedVersions.filter(id => id !== versionId));
    } else {
      if (selectedVersions.length < 2) {
        setSelectedVersions([...selectedVersions, versionId]);
      } else {
        alert('You can only compare 2 versions at a time');
      }
    }
  };

  const handleCompareVersions = () => {
    if (selectedVersions.length === 2) {
      setShowCompareModal(true);
    } else {
      alert('Please select exactly 2 versions to compare');
    }
  };

  const handleCreateNewVersion = () => {
    setShowCreateVersionModal(true);
  };

  const handleRestoreVersion = (versionId) => {
    if (window.confirm('Are you sure you want to restore this version? This will create a new version based on the selected one.')) {
      alert(`Version ${versionId} restored successfully!`);
    }
  };

  const handleViewVersion = (versionId) => {
    const version = versions.find(v => v.version === versionId);
    if (version) {
      setSelectedVersion(version);
      setShowViewModal(true);
    }
  };

  const handleEditVersion = (version) => {
    setEditingVersion({ ...version });
    setShowViewModal(false);
    setShowEditModal(true);
  };

  // Fixed save function to actually update the data
  const handleSaveEdit = () => {
    // Validate required fields
    const requiredFields = ['changes'];
    const validation = validateRequiredFields(editingVersion, requiredFields);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      showNotification('Please fill all required fields', 'error');
      return;
    }
    
    // Validate items
    let hasItemErrors = false;
    editingVersion.items.forEach((item, index) => {
      if (!item.name || item.name.trim() === '') {
        hasItemErrors = true;
        showNotification(`Item ${index + 1}: Product name is required`, 'error');
      }
      if (item.quantity <= 0) {
        hasItemErrors = true;
        showNotification(`Item ${index + 1}: Quantity must be greater than 0`, 'error');
      }
      if (item.unitPrice < 0) {
        hasItemErrors = true;
        showNotification(`Item ${index + 1}: Unit price must be 0 or greater`, 'error');
      }
    });
    
    if (hasItemErrors) {
      return;
    }
    
    // Update the versions array with the edited version
    const updatedVersions = versions.map(version => 
      version.version === editingVersion.version ? editingVersion : version
    );
    setVersions(updatedVersions);
    
    // Close the modal and clear editing state
    setShowEditModal(false);
    setEditingVersion(null);
    setErrors({});
    
    // Show success message
    showNotification('Version updated successfully!', 'success');
  };

  const updateEditingItem = (index, field, value) => {
    const updatedItems = [...editingVersion.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    if (field === 'quantity' || field === 'unitPrice') {
      updatedItems[index].totalPrice = updatedItems[index].quantity * updatedItems[index].unitPrice;
    }
    
    const totalValue = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);
    
    setEditingVersion({
      ...editingVersion,
      items: updatedItems,
      totalValue,
      itemsCount: updatedItems.length
    });
    
    // Clear error for this field
    if (errors[`item_${index}_${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`item_${index}_${field}`];
        return newErrors;
      });
    }
  };

  // Updated PDF download function
  const handleDownloadVersion = async (versionId) => {
    setDownloadingVersion(versionId);
    
    try {
      const version = versions.find(v => v.version === versionId);
      if (!version) {
        throw new Error('Version not found');
      }

      const pdfContent = generateMockPDFContent(version);
      const blob = new Blob([pdfContent], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quote_${quote.id}_Version_${versionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingVersion(null);
    }
  };

  const generateMockPDFContent = (version) => {
    const content = `
QUOTATION - ${quote.title}
==============================

Quote ID: ${quote.id}
Version: ${version.version}
Customer: ${quote.customer}
Created By: ${version.createdBy}
Created At: ${version.createdAt}
Status: ${version.status}

ITEMS:
------
${version.items.map(item => 
  `${item.name} - Qty: ${item.quantity} - Unit: ₹${item.unitPrice.toLocaleString()} - Total: ₹${item.totalPrice.toLocaleString()}`
).join('\n')}

TOTAL VALUE: ₹${version.totalValue.toLocaleString()}

TERMS & CONDITIONS:
------------------
${version.terms}

CHANGES MADE:
------------
${version.changes}
    `;
    
    return content;
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    showNotification('Refreshing version history...', 'info');
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Simulate data refresh by updating timestamps and status
      const updatedVersions = versions.map(version => ({
        ...version,
        createdAt: new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
        // Simulate some status changes
        status: version.status === 'Sent' && Math.random() > 0.7 ? 'Seen' : version.status
      }));
      
      setVersions(updatedVersions);
      showNotification('Version history refreshed successfully!', 'success');
      
    } catch (error) {
      showNotification('Failed to refresh data. Please try again.', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  const selectedVersionsData = versions.filter(v => selectedVersions.includes(v.id));

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-500 text-white' :
          notification.type === 'error' ? 'bg-red-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          <div className="flex items-center space-x-2">
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 text-white hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Version History</h1>
            <p className="text-gray-600">Quote ID: {quote.id}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            icon={isRefreshing ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div> : <RefreshCw className="h-4 w-4" />}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={handleCreateNewVersion}
            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
          >
            Create New Version
          </Button>
        </div>
      </div>

      {/* Quote Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Quote Information</h3>
            <p className="text-sm text-gray-600">Customer: {quote.customer}</p>
            <p className="text-sm text-gray-600">Title: {quote.title}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Version Summary</h3>
            <p className="text-sm text-gray-600">Current Version: v{quote.currentVersion}</p>
            <p className="text-sm text-gray-600">Total Versions: {quote.totalVersions}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Quick Actions</h3>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                icon={<Eye className="h-4 w-4" />}
                onClick={() => handleViewVersion(quote.currentVersion)}
              >
                View Current
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<Download className="h-4 w-4" />}
                onClick={() => handleDownloadVersion(quote.currentVersion)}
                disabled={downloadingVersion === quote.currentVersion}
              >
                {downloadingVersion === quote.currentVersion ? 'Downloading...' : 'Download'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Version List */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
            {selectedVersions.length === 2 && (
              <Button
                variant="primary"
                icon={<Eye className="h-4 w-4" />}
                onClick={handleCompareVersions}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                Compare Selected
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedVersions(versions.slice(0, 2).map(v => v.id));
                      } else {
                        setSelectedVersions([]);
                      }
                    }}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Version</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Changes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {versions.map((version) => (
                <tr key={version.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedVersions.includes(version.id)}
                      onChange={() => handleVersionSelect(version.id)}
                      className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <GitBranch className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">v{version.version}</span>
                      {version.version === quote.currentVersion && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Current
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQuotationStatusClasses(version.status)}`}>
                      {version.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {version.createdBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {version.createdAt}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                    {version.changes}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₹{version.totalValue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Eye className="h-4 w-4" />}
                        onClick={() => handleViewVersion(version.version)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Download className="h-4 w-4" />}
                        onClick={() => handleDownloadVersion(version.version)}
                        disabled={downloadingVersion === version.version}
                      >
                        {downloadingVersion === version.version ? 'Downloading...' : 'Download'}
                      </Button>
                      {version.version !== quote.currentVersion && (
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<RotateCcw className="h-4 w-4" />}
                          onClick={() => handleRestoreVersion(version.version)}
                        >
                          Restore
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Communication Log Summary */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Communication Summary</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {versions.map((version) => (
              <div key={version.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Version {version.version}</h4>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQuotationStatusClasses(version.status)}`}>
                    {version.status}
                  </span>
                </div>
                <div className="space-y-2">
                  {version.communications.map((comm, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600">{comm.channel}:</span>
                        <span className="text-gray-900">{comm.sentAt}</span>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        comm.status === 'Delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {comm.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* View Version Modal */}
      {showViewModal && selectedVersion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                <h3 className="text-xl font-semibold text-gray-900">Version {selectedVersion.version} Details</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQuotationStatusClasses(selectedVersion.status)}`}>
                  {selectedVersion.status}
                </span>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Version Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Created By</p>
                  <p className="text-lg text-gray-900">{selectedVersion.createdBy}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Created At</p>
                  <p className="text-lg text-gray-900">{selectedVersion.createdAt}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Changes Made</p>
                  <p className="text-lg text-gray-900">{selectedVersion.changes}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Total Value</p>
                  <p className="text-2xl font-bold text-agrosaf-green">₹{selectedVersion.totalValue.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Items Count</p>
                  <p className="text-lg text-gray-900">{selectedVersion.itemsCount} items</p>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Quote Items</h4>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedVersion.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.quantity}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">₹{item.unitPrice.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">₹{item.totalPrice.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Terms & Conditions</h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">{selectedVersion.terms}</p>
              </div>
            </div>

            {/* Communications */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Communications</h4>
              <div className="space-y-2">
                {selectedVersion.communications.map((comm, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-900">{comm.channel}</span>
                      <span className="text-sm text-gray-600">{comm.sentAt}</span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      comm.status === 'Delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {comm.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </Button>
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={() => handleDownloadVersion(selectedVersion.version)}
                disabled={downloadingVersion === selectedVersion.version}
              >
                {downloadingVersion === selectedVersion.version ? 'Downloading...' : 'Download PDF'}
              </Button>
              <Button
                variant="primary"
                icon={<Edit className="h-4 w-4" />}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                onClick={() => handleEditVersion(selectedVersion)}
              >
                Edit Version
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Version Modal */}
      {showEditModal && editingVersion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
              <h3 className="text-xl font-semibold text-gray-900">Edit Version {editingVersion.version}</h3>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQuotationStatusClasses(editingVersion.status)}`}>
                  {editingVersion.status}
                </span>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Editable Items */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Edit Quote Items</h4>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {editingVersion.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3">
                          <div className="relative">
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => {
                                const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.productName);
                                updateEditingItem(index, 'name', sanitized);
                              }}
                              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm ${
                                errors[`item_${index}_name`] ? 'border-red-300' : 'border-gray-300'
                              }`}
                              maxLength={FIELD_LIMITS.productName}
                              required
                            />
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                              {getCharacterCount(item.name || '', FIELD_LIMITS.productName)}
                            </div>
                          </div>
                          {errors[`item_${index}_name`] && (
                            <p className="text-xs text-red-600 mt-1">{errors[`item_${index}_name`]}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const validated = handleNumberInputChange(e, null, NUMBER_LIMITS.quantity.max, NUMBER_LIMITS.quantity.min);
                              updateEditingItem(index, 'quantity', validated);
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm ${
                              errors[`item_${index}_quantity`] ? 'border-red-300' : 'border-gray-300'
                            }`}
                            min={NUMBER_LIMITS.quantity.min}
                            max={NUMBER_LIMITS.quantity.max}
                            required
                          />
                          {errors[`item_${index}_quantity`] && (
                            <p className="text-xs text-red-600 mt-1">{errors[`item_${index}_quantity`]}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => {
                              const validated = handleNumberInputChange(e, null, NUMBER_LIMITS.unitPrice.max, NUMBER_LIMITS.unitPrice.min);
                              updateEditingItem(index, 'unitPrice', validated);
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm ${
                              errors[`item_${index}_unitPrice`] ? 'border-red-300' : 'border-gray-300'
                            }`}
                            min={NUMBER_LIMITS.unitPrice.min}
                            max={NUMBER_LIMITS.unitPrice.max}
                            required
                          />
                          {errors[`item_${index}_unitPrice`] && (
                            <p className="text-xs text-red-600 mt-1">{errors[`item_${index}_unitPrice`]}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          ₹{item.totalPrice.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-right">
                <p className="text-lg font-bold text-agrosaf-green">
                  Total Value: ₹{editingVersion.totalValue.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Editable Terms */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Edit Terms & Conditions</h4>
              <div className="relative">
                <textarea
                  rows={4}
                  value={editingVersion.terms}
                  onChange={(e) => {
                    const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.terms);
                    setEditingVersion({ ...editingVersion, terms: sanitized });
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm ${
                    errors.terms ? 'border-red-300' : 'border-gray-300'
                  }`}
                  maxLength={FIELD_LIMITS.terms}
                />
                <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                  {getCharacterCount(editingVersion.terms || '', FIELD_LIMITS.terms)}
                </div>
              </div>
              {errors.terms && (
                <p className="text-xs text-red-600 mt-1">{errors.terms}</p>
              )}
            </div>

            {/* Editable Changes */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Edit Changes Description <span className="text-red-500">*</span>
              </h4>
              <div className="relative">
                <textarea
                  rows={3}
                  value={editingVersion.changes}
                  onChange={(e) => {
                    const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                    setEditingVersion({ ...editingVersion, changes: sanitized });
                    if (errors.changes) {
                      setErrors(prev => ({ ...prev, changes: '' }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm ${
                    errors.changes ? 'border-red-300' : 'border-gray-300'
                  }`}
                  maxLength={FIELD_LIMITS.notes}
                  required
                />
                <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                  {getCharacterCount(editingVersion.changes || '', FIELD_LIMITS.notes)}
                </div>
              </div>
              {errors.changes && (
                <p className="text-xs text-red-600 mt-1">{errors.changes}</p>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                onClick={handleSaveEdit}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Compare Versions Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Compare Versions</h3>
              <button
                onClick={() => setShowCompareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {selectedVersionsData.map((version, index) => (
                <div key={version.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">Version {version.version}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQuotationStatusClasses(version.status)}`}>
                      {version.status}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Created By</p>
                      <p className="font-medium text-gray-900">{version.createdBy}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Created At</p>
                      <p className="font-medium text-gray-900">{version.createdAt}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Changes</p>
                      <p className="font-medium text-gray-900">{version.changes}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Value</p>
                      <p className="font-medium text-gray-900">₹{version.totalValue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Items Count</p>
                      <p className="font-medium text-gray-900">{version.itemsCount}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCompareModal(false)}
              >
                Close
              </Button>
              <Button
                variant="primary"
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                onClick={() => {
                  alert('Detailed comparison exported successfully!');
                  setShowCompareModal(false);
                }}
              >
                Export Comparison
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create New Version Modal */}
      {showCreateVersionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Version</h3>
              <button
                onClick={() => {
                  setShowCreateVersionModal(false);
                  setNewVersionNotes('');
                  setErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Version
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent">
                  <option value="current">Current Version (v{quote.currentVersion})</option>
                  {versions.map((version) => (
                    <option key={version.id} value={version.version}>Version {version.version}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Version Notes <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <textarea
                    rows={3}
                    value={newVersionNotes}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, setNewVersionNotes, FIELD_LIMITS.versionNotes);
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${
                      errors.versionNotes ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Describe the changes for this new version..."
                    maxLength={FIELD_LIMITS.versionNotes}
                    required
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                    {getCharacterCount(newVersionNotes, FIELD_LIMITS.versionNotes)}
                  </div>
                </div>
                {errors.versionNotes && (
                  <p className="text-xs text-red-600 mt-1">{errors.versionNotes}</p>
                )}
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm text-yellow-800">
                      Creating a new version will copy the selected base version and allow you to make modifications.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCreateVersionModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                onClick={() => {
                  if (!newVersionNotes || newVersionNotes.trim() === '') {
                    setErrors({ versionNotes: 'Version notes is required' });
                    showNotification('Please enter version notes', 'error');
                    return;
                  }
                  showNotification('New version created successfully!', 'success');
                  setShowCreateVersionModal(false);
                  setNewVersionNotes('');
                  setErrors({});
                  navigate(`/crm/quotation-proposal/quote-builder/${quote.id}`);
                }}
              >
                Create Version
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistory;
